import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronDown,
  TrendingUp,
  MapPin,
  Filter,
  FileText,
  Map,
  Heart,
  Download,
  ArrowRight,
  Home,
  Layers,
  Users,
  CreditCard,
  BookOpen,
  Menu,
  X
} from 'lucide-react'
import './Navbar.css'

/**
 * Public website Navbar - only for unauthenticated users
 * Authenticated users are redirected to dashboard and should not see this
 */

const FEATURES_MENU = [
  { path: '/features/pre-analysed-deals', label: 'Pre-Analysed Deals', icon: TrendingUp, description: 'Yield estimates & deal scoring' },
  { path: '/features/local-area-data', label: 'Local Area Intelligence', icon: MapPin, description: 'Crime, flood & transport data' },
  { path: '/features/dashboard-insights', label: 'Dashboard Insights', icon: Filter, description: 'Strategy hotspots & value finder' },
  { path: '/features/land-registry', label: 'Land Registry Data', icon: FileText, description: 'Real sale prices & comparables' },
  { path: '/features/interactive-map', label: 'Interactive Map', icon: Map, description: 'Visualise deals & amenities' },
  { path: '/features/favourites', label: 'Save & Organise', icon: Heart, description: 'Build your deal pipeline' },
  { path: '/features/pdf-reports', label: 'PDF Reports', icon: Download, description: 'Professional deal reports' },
]

export const Navbar: React.FC = () => {
  const location = useLocation()
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const isActive = (path: string) => location.pathname === path
  const isFeaturesActive = location.pathname.startsWith('/features')

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setFeaturesOpen(true)
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setFeaturesOpen(false)
    }, 150)
  }

  return (
    <header className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="Dealigent" className="logo-image" />
          <div className="logo-text-container">
            <p className="logo-subtitle">Property Deal Intelligence</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="navbar-actions navbar-desktop">
          <div className="navbar-menu">
            <Link to="/" className={`navbar-link ${isActive('/') ? 'active' : ''}`}>
              <Home size={16} />
              Home
            </Link>

            {/* Features Dropdown */}
            <div
              className="navbar-dropdown"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`navbar-link navbar-dropdown-trigger ${isFeaturesActive ? 'active' : ''} ${featuresOpen ? 'open' : ''}`}
              >
                <Layers size={16} />
                Features
                <ChevronDown size={16} />
              </button>
              <div className={`navbar-dropdown-menu navbar-dropdown-mega ${featuresOpen ? 'open' : ''}`}>
                <div className="navbar-dropdown-grid">
                  {FEATURES_MENU.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`navbar-dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => setFeaturesOpen(false)}
                    >
                      <item.icon size={20} className="navbar-dropdown-icon" />
                      <div className="navbar-dropdown-item-content">
                        <span className="navbar-dropdown-item-label">{item.label}</span>
                        <span className="navbar-dropdown-item-desc">{item.description}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="navbar-dropdown-footer">
                  <Link to="/features" className="navbar-dropdown-all" onClick={() => setFeaturesOpen(false)}>
                    View all features
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/pricing" className={`navbar-link ${isActive('/pricing') ? 'active' : ''}`}>
              <CreditCard size={16} />
              Pricing
            </Link>

            <Link to="/knowledge-hub" className={`navbar-link ${location.pathname.startsWith('/knowledge-hub') ? 'active' : ''}`}>
              <BookOpen size={16} />
              Knowledge Hub
            </Link>

            <Link to="/about" className={`navbar-link ${isActive('/about') ? 'active' : ''}`}>
              <Users size={16} />
              About
            </Link>
          </div>

          <Link to="/login" className="btn-login">
            Sign In
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="navbar-mobile-toggle">
          <Link to="/login" className="btn-login">
            Sign In
          </Link>
          <button
            className="navbar-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="navbar-mobile-menu">
            <Link to="/" className={`navbar-mobile-link ${isActive('/') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <Home size={18} />
              Home
            </Link>
            <Link to="/features" className={`navbar-mobile-link ${isFeaturesActive ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <Layers size={18} />
              Features
            </Link>
            {FEATURES_MENU.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`navbar-mobile-link navbar-mobile-sublink ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon size={16} />
                {item.label}
              </Link>
            ))}
            <Link to="/pricing" className={`navbar-mobile-link ${isActive('/pricing') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <CreditCard size={18} />
              Pricing
            </Link>
            <Link to="/knowledge-hub" className={`navbar-mobile-link ${location.pathname.startsWith('/knowledge-hub') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <BookOpen size={18} />
              Knowledge Hub
            </Link>
            <Link to="/about" className={`navbar-mobile-link ${isActive('/about') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <Users size={18} />
              Pricing
            </Link>
          </div>
        )}
      </div>
    </header>
  )
}
